/**
 * Clients (features.onboarding): #/clients, #/clients/new and #/clients/<id>. The office creates a client
 * with a few details (or converts a website lead) and sends a welcome pack; the client confirms their
 * business details and signs the terms; the office sees where each client is up to.
 */
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { FilterBar, PageHeader, SearchField, SelectField, plural } from "../../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton, type ChipTone } from "../../components/States";
import { BRAND } from "../../lib/config";
import { fmtDate } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { hashParams, telHref, useHash } from "../../lib/route";
import { useAsync } from "../../lib/useAsync";
import {
  accountAgreementPdf,
  addSiteContact,
  cancelWelcomePack,
  clientState,
  createClient,
  getClient,
  latestTermsId,
  listClients,
  removeSiteContact,
  sendWelcomePack,
  STATE_LABEL,
  updateClient,
  type ClientDetail,
  type ClientFields,
  type ClientState,
} from "./clients-data";

const STATE_TONE: Record<ClientState, ChipTone> = {
  not_sent: "outline",
  details_requested: "grey",
  opened: "accent",
  active: "green",
  terms_out_of_date: "primary",
  link_expired: "primary-outline",
};
export const stateChip = (s: ClientState) => <Chip tone={STATE_TONE[s]}>{STATE_LABEL[s]}</Chip>;
const termsLower = () => BRAND.termsTitle.charAt(0).toLowerCase() + BRAND.termsTitle.slice(1);

export function Clients() {
  const [id] = hashParams(useHash());
  if (id === "new") return <NewClient />;
  if (id) return <ClientView key={id} id={id} />;
  return <ClientList />;
}

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return (
    <label className={`field${wide ? " field--wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

/* ------------------------------------------------------------------ list */

function ClientList() {
  const { data, error, loading, reload } = useAsync(async () => ({ clients: await listClients(), latest: await latestTermsId() }));
  const [q, setQ] = useState("");
  const [show, setShow] = useState("all");
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.clients ?? [])
      .map((c) => ({ c, state: clientState(c, data!.latest) }))
      .filter(({ c, state }) => {
        if (show === "waiting" && !["details_requested", "opened", "link_expired"].includes(state)) return false;
        if (show === "active" && state !== "active") return false;
        if (show === "attention" && !["terms_out_of_date", "link_expired", "not_sent"].includes(state)) return false;
        return !needle || [c.name, c.legal_name, c.contact_name, c.email].some((s) => s?.toLowerCase().includes(needle));
      });
  }, [data, q, show]);

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Operations"
        title="Clients"
        meta={`Create a client with a name, email and mobile, then send a welcome pack. The client confirms their business details and signs your ${termsLower()} from one link.`}
        actions={<a className="btn btn--primary" href="#/clients/new"><Icon name="plus" size={18} /> New client</a>}
      />
      <FilterBar count={data ? `Showing ${plural(rows.length, "client")}` : undefined}>
        <SearchField label="Search clients" placeholder="Search name, contact or email" value={q} onChange={setQ} />
        <SelectField label="Show" value={show} onChange={setShow} options={[
          { value: "all", label: "All clients" },
          { value: "waiting", label: "Waiting on the client" },
          { value: "attention", label: "Needs a welcome pack" },
          { value: "active", label: "Active, terms signed" },
        ]} />
      </FilterBar>
      {loading && !data && <Skeleton columns={4} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && rows.length === 0 && <EmptyState icon="inbox" title={data.clients.length ? "No matching clients" : "No clients yet"}>{data.clients.length ? "Try a different search or filter." : "Create one, or turn a website lead into a client from Leads."}</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack">
            <thead><tr><th scope="col">Client</th><th scope="col">Contact</th><th scope="col">Status</th><th scope="col">Last activity</th></tr></thead>
            <tbody>
              {rows.map(({ c, state }) => {
                const last = c.onboarding_requests.reduce<string>((m, r) => [r.completed_at, r.opened_at, r.sent_at].filter((x): x is string => Boolean(x)).reduce((a, b) => (a > b ? a : b), m), c.updated_at);
                return (
                  <tr key={c.id}>
                    <td data-label="Client"><a className="strong" href={`#/clients/${c.id}`}>{c.name}</a>{c.legal_name && c.legal_name !== c.name && <span className="sub">{c.legal_name}</span>}</td>
                    <td data-label="Contact">{c.contact_name ?? <span className="muted">None</span>}<span className="sub">{c.email ?? c.phone ?? ""}</span></td>
                    <td data-label="Status">{stateChip(state)}</td>
                    <td data-label="Last activity" className="nowrap">{fmtDate(last)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ new */

function NewClient() {
  const [f, setF] = useState({ name: "", contact_name: "", email: "", phone: "", payment_terms_days: String(BRAND.onboarding?.paymentTermsDays ?? 14) });
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!f.contact_name.trim()) return setNotice({ tone: "error", text: "Add the contact's name." });
    if (!f.email.trim() && !f.phone.trim()) return setNotice({ tone: "error", text: "Add an email or a mobile. The welcome pack needs an email." });
    setBusy(true);
    try {
      const id = await createClient({
        name: f.name.trim() || f.contact_name.trim(), contact_name: f.contact_name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null,
        payment_terms_days: Number(f.payment_terms_days) || 0,
      });
      window.location.hash = `#/clients/${id}`;
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }
  return (
    <section className="page" aria-labelledby="page-title">
      <a className="backlink" href="#/clients"><Icon name="arrow" size={16} /> All clients</a>
      <PageHeader eyebrow="Clients" title="New client" meta="Just enough to send the welcome pack. The client fills in the rest." />
      <div className="card"><div className="card__body">
        <form className="form" onSubmit={submit}>
          <Notice notice={notice} onDismiss={() => setNotice(null)} />
          <div className="form-grid">
            <Field label="Contact name"><input className="input" required value={f.contact_name} onChange={set("contact_name")} autoComplete="off" /></Field>
            <Field label="Company" hint="Optional. The client confirms the legal name."><input className="input" value={f.name} onChange={set("name")} /></Field>
            <Field label="Email" hint="Where the welcome pack goes"><input className="input" type="email" value={f.email} onChange={set("email")} /></Field>
            <Field label="Mobile"><input className="input" type="tel" value={f.phone} onChange={set("phone")} /></Field>
            <Field label="Payment terms (days)" hint="Shown to the client in the welcome pack"><input className="input" type="number" min={0} max={120} value={f.payment_terms_days} onChange={set("payment_terms_days")} /></Field>
          </div>
          <div className="form-actions"><button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>{busy ? "Saving" : "Create client"}</button></div>
        </form>
      </div></div>
    </section>
  );
}

/* ------------------------------------------------------------------ record */

function ClientView({ id }: { id: string }) {
  const { data, error, loading, reload } = useAsync(async () => ({ c: await getClient(id), latest: await latestTermsId() }), [id]);
  const [editing, setEditing] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();

  if (loading && !data) return <section className="page"><Skeleton rows={4} columns={2} /></section>;
  if (error) return <section className="page"><ErrorState message={error} onRetry={reload} /></section>;
  if (!data?.c) return <section className="page"><EmptyState icon="search" title="Client not found"><a href="#/clients">Back to clients</a></EmptyState></section>;
  const { c, latest } = data;
  const state = clientState(c, latest);
  const none = <span className="muted">Not given</span>;

  return (
    <section className="page job-grid" aria-labelledby="page-title">
      <a className="backlink" href="#/clients"><Icon name="arrow" size={16} /> All clients</a>
      <PageHeader eyebrow="Client" title={c.name} meta={[c.contact_name, c.email, c.phone].filter(Boolean).join(" · ") || undefined}
        actions={!editing && <button type="button" className="btn btn--outline" onClick={() => setEditing(true)}>Edit details</button>} />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <WelcomePack c={c} state={state} onDone={(text) => { ok(text); reload(); }} onError={fail} />

      <div className="card">
        <div className="card__head"><h2 className="card__title">Details</h2>{c.details_verified_at && <span className="small muted">Confirmed by the client {fmtDate(c.details_verified_at)}</span>}</div>
        <div className="card__body">
          {editing ? <ClientForm c={c} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); ok("Details saved."); reload(); }} /> : (
            <dl className="facts">
              <div className="fact"><dt>Legal name</dt><dd>{c.legal_name ?? none}</dd></div>
              <div className="fact"><dt>Trading name</dt><dd>{c.trading_name ?? none}</dd></div>
              <div className="fact"><dt>ABN</dt><dd>{c.abn ? c.abn.replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3 $4") : none}</dd></div>
              <div className="fact"><dt>Phone</dt><dd>{c.phone ? <a className="tel" href={telHref(c.phone)}>{c.phone}</a> : none}</dd></div>
              <div className="fact fact--wide"><dt>Business address</dt><dd>{c.billing_address ?? none}</dd></div>
              <div className="fact"><dt>Accounts contact</dt><dd>{c.accounts_contact_name ?? none}</dd></div>
              <div className="fact"><dt>Accounts email</dt><dd>{c.accounts_email ?? none}</dd></div>
              <div className="fact"><dt>Send invoices to</dt><dd>{c.invoice_email ?? c.accounts_email ?? none}</dd></div>
              <div className="fact"><dt>Purchase order number</dt><dd>{c.po_required ? "Needed on invoices" : "Not needed"}</dd></div>
              <div className="fact"><dt>Payment terms</dt><dd>{c.payment_terms_days === 0 ? "Due on invoice" : `${c.payment_terms_days} days`}</dd></div>
              {c.details_verified_at && <div className="fact fact--wide"><dt>Note</dt><dd className="small muted">These details were entered and confirmed by the client. If you change them, the signed agreement still shows what the client confirmed.</dd></div>}
            </dl>
          )}
        </div>
      </div>

      <SiteContacts c={c} onChange={reload} onError={fail} />

      <div className="card">
        <div className="card__head"><h2 className="card__title">Agreements</h2></div>
        {c.account_agreements.length === 0 ? <p className="card__body muted">No signed {termsLower()} yet.</p> : (
          <ul className="list">
            {c.account_agreements.map((a) => (
              <li key={a.id} className="list__row">
                <span>
                  <strong>{BRAND.termsTitle}</strong> signed by {a.signer_name}, {a.signer_position}
                  <span className="sub">{a.signed_at_local} · {a.payment_terms_days} day payment terms{a.terms_version_id === latest ? " · current version" : " · an older version"}</span>
                </span>
                <button type="button" className="btn btn--outline btn--xs" onClick={() => void openBlobInNewTab(() => accountAgreementPdf(a.id)).catch(fail)}>Open PDF</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">Jobs and quotes</h2></div>
        {c.jobs.length + c.quotes.length === 0 ? <p className="card__body muted">No jobs or quotes linked to this client yet.</p> : (
          <ul className="list">
            {c.jobs.map((j) => <li key={j.id} className="list__row"><a href={`#/jobs/${j.id}`}><strong>{j.reference}</strong> {j.title}</a><Chip value={j.status} /></li>)}
            {c.quotes.map((q) => <li key={q.id} className="list__row"><a href={`#/quotes/${q.id}`}><strong>{q.quote_number}</strong> {q.job_title ?? ""}</a><Chip value={q.status} /></li>)}
          </ul>
        )}
      </div>
    </section>
  );
}

function WelcomePack({ c, state, onDone, onError }: { c: ClientDetail; state: ClientState; onDone: (text: string) => void; onError: (e: unknown) => void }) {
  const live = c.onboarding_requests.find((r) => r.status === "sent" || r.status === "opened");
  const [terms, setTerms] = useState(String(c.payment_terms_days));
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const sendLabel = state === "terms_out_of_date" ? "Send the new terms" : live ? "Resend" : "Send welcome pack";

  async function send() {
    setBusy(true);
    try {
      const r = await sendWelcomePack(c.id, { paymentTermsDays: Number(terms) });
      setLink(r.link);
      onDone(r.emailed ? `Welcome pack emailed to ${c.email}.` : `The email could not be sent (${r.emailError}). Copy the link below and text it instead.`);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  async function cancel() {
    if (!live) return;
    setBusy(true);
    try {
      await cancelWelcomePack(live.id);
      onDone("Welcome pack cancelled. The link no longer works.");
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`card${state === "terms_out_of_date" || state === "link_expired" ? " card--alert" : ""}`}>
      <div className="card__head"><h2 className="card__title">Welcome pack</h2>{stateChip(state)}</div>
      <div className="card__body stack">
        {live && (
          <p className="small">
            Sent to {live.recipient_name} ({live.recipient_email}) {fmtDate(live.sent_at)}. {live.opened_at ? `Opened ${fmtDate(live.opened_at)}.` : "Not opened yet."} The link works until {fmtDate(live.expires_at)}.
          </p>
        )}
        {state === "active" && <p className="small">The client confirmed their details and signed your current {termsLower()}. Their quotes are accepted under them, and a written job confirmation is enough to confirm a booking.</p>}
        {state === "terms_out_of_date" && <p className="small">You have published new {termsLower()} since this client signed. Their next quote asks them to accept the new version, or send the new terms now.</p>}
        {state !== "active" && (
          <div className="addcrew">
            <label className="field field--inline">
              <span className="field__label">Payment terms (days)</span>
              <input className="input input--compact" type="number" min={0} max={120} value={terms} onChange={(e) => setTerms(e.target.value)} />
            </label>
            <button type="button" className="btn btn--primary btn--sm" disabled={busy || !c.email} aria-busy={busy} onClick={() => void send()}>{sendLabel}</button>
            {live && <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => void cancel()}>Cancel link</button>}
          </div>
        )}
        {!c.email && <p className="small sub--primary">Add the client's email (Edit details) to send the welcome pack.</p>}
        {link && (
          <div className="stack" style={{ gap: 6 }}>
            <span className="small">The link (shown once, for texting it):</span>
            <div className="addcrew">
              <input className="input" readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Welcome pack link" />
              <button type="button" className="btn btn--outline btn--sm" onClick={() => void navigator.clipboard.writeText(link)}>Copy</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ClientForm({ c, onSaved, onCancel }: { c: ClientDetail; onSaved: () => void; onCancel: () => void }) {
  const [f, setF] = useState({
    name: c.name, contact_name: c.contact_name ?? "", email: c.email ?? "", phone: c.phone ?? "", legal_name: c.legal_name ?? "", trading_name: c.trading_name ?? "",
    abn: c.abn ?? "", billing_address: c.billing_address ?? "", accounts_contact_name: c.accounts_contact_name ?? "", accounts_email: c.accounts_email ?? "",
    invoice_email: c.invoice_email ?? "", po_required: c.po_required, payment_terms_days: String(c.payment_terms_days),
  });
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  const set = (k: Exclude<keyof typeof f, "po_required">) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const blank = (s: string) => s.trim() || null;
  async function submit(e: FormEvent) {
    e.preventDefault();
    const abn = f.abn.replace(/\s/g, "");
    if (abn && !/^\d{11}$/.test(abn)) return setNotice({ tone: "error", text: "An ABN is 11 digits." });
    setBusy(true);
    try {
      const fields: ClientFields = {
        name: f.name.trim() || c.name, contact_name: blank(f.contact_name), email: blank(f.email)?.toLowerCase() ?? null, phone: blank(f.phone), legal_name: blank(f.legal_name),
        trading_name: blank(f.trading_name), abn: abn || null, billing_address: blank(f.billing_address), accounts_contact_name: blank(f.accounts_contact_name),
        accounts_email: blank(f.accounts_email), invoice_email: blank(f.invoice_email), po_required: f.po_required, payment_terms_days: Number(f.payment_terms_days) || 0,
      };
      await updateClient(c.id, fields);
      onSaved();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }
  return (
    <form className="form" onSubmit={submit}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="form-grid">
        <Field label="Client name (as you know them)"><input className="input" value={f.name} onChange={set("name")} /></Field>
        <Field label="Contact name"><input className="input" value={f.contact_name} onChange={set("contact_name")} /></Field>
        <Field label="Email"><input className="input" type="email" value={f.email} onChange={set("email")} /></Field>
        <Field label="Phone"><input className="input" type="tel" value={f.phone} onChange={set("phone")} /></Field>
        <Field label="Legal name"><input className="input" value={f.legal_name} onChange={set("legal_name")} /></Field>
        <Field label="Trading name"><input className="input" value={f.trading_name} onChange={set("trading_name")} /></Field>
        <Field label="ABN"><input className="input" inputMode="numeric" value={f.abn} onChange={set("abn")} /></Field>
        <Field label="Payment terms (days)"><input className="input" type="number" min={0} max={120} value={f.payment_terms_days} onChange={set("payment_terms_days")} /></Field>
        <Field label="Business address" wide><input className="input" value={f.billing_address} onChange={set("billing_address")} /></Field>
        <Field label="Accounts contact"><input className="input" value={f.accounts_contact_name} onChange={set("accounts_contact_name")} /></Field>
        <Field label="Accounts email"><input className="input" type="email" value={f.accounts_email} onChange={set("accounts_email")} /></Field>
        <Field label="Send invoices to"><input className="input" type="email" value={f.invoice_email} onChange={set("invoice_email")} /></Field>
      </div>
      <label className="check"><input type="checkbox" checked={f.po_required} onChange={(e) => setF((x) => ({ ...x, po_required: e.target.checked }))} /> Purchase order number needed on invoices</label>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>{busy ? "Saving" : "Save"}</button>
        <button type="button" className="btn btn--outline" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

function SiteContacts({ c, onChange, onError }: { c: ClientDetail; onChange: () => void; onError: (e: unknown) => void }) {
  const [f, setF] = useState({ name: "", mobile: "", email: "", role: "" });
  const [busy, setBusy] = useState(false);
  async function add(e: FormEvent) {
    e.preventDefault();
    if (!f.name.trim()) return;
    setBusy(true);
    try {
      await addSiteContact(c.id, { name: f.name.trim(), mobile: f.mobile.trim() || null, email: f.email.trim() || null, role: f.role.trim() || null });
      setF({ name: "", mobile: "", email: "", role: "" });
      onChange();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">Site contacts</h2></div>
      <ul className="list">
        {c.customer_site_contacts.map((s) => (
          <li key={s.id} className="list__row">
            <span><strong>{s.name}</strong>{s.role ? `, ${s.role}` : ""}<span className="sub">{[s.mobile, s.email].filter(Boolean).join(" · ")}</span></span>
            <span className="actions">
              {s.mobile && <a className="tel" href={telHref(s.mobile)}>Call</a>}
              <button type="button" className="btn btn--outline btn--xs" onClick={() => void removeSiteContact(s.id).then(onChange, onError)}>Remove</button>
            </span>
          </li>
        ))}
        {c.customer_site_contacts.length === 0 && <li className="list__row muted">No site contacts yet.</li>}
      </ul>
      <form className="card__body addcrew" onSubmit={add}>
        <input className="input" aria-label="Site contact name" placeholder="Name" value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} />
        <input className="input" aria-label="Mobile" placeholder="Mobile" type="tel" value={f.mobile} onChange={(e) => setF((x) => ({ ...x, mobile: e.target.value }))} />
        <input className="input" aria-label="Role" placeholder="Role" value={f.role} onChange={(e) => setF((x) => ({ ...x, role: e.target.value }))} />
        <button type="submit" className="btn btn--ink btn--sm" disabled={busy || !f.name.trim()}>Add</button>
      </form>
    </div>
  );
}
